import { useState, useEffect, useRef, useCallback } from 'react';
import { notificationAPI } from '../services/api';
import { playNotificationSound } from '../utils/notificationSound';

export default function useNotifications() {
  const [notifications, setNotifications] = useState([]);
  const [config, setConfig] = useState(null);
  const [permissionState, setPermissionState] = useState('default');
  const pollRef = useRef(null);
  const configLoadedRef = useRef(false);

  // Load config once
  useEffect(() => {
    if (configLoadedRef.current) return;
    configLoadedRef.current = true;
    notificationAPI.getConfig()
      .then(c => setConfig(c))
      .catch(err => console.warn('Failed to load notification config:', err));
  }, []);

  // Request browser notification permission
  useEffect(() => {
    if ('Notification' in window) {
      setPermissionState(Notification.permission);
      if (Notification.permission === 'default') {
        Notification.requestPermission().then(perm => setPermissionState(perm));
      }
    }
  }, []);

  // Deliver a notification to the user via enabled channels
  const deliverNotification = useCallback(async (notif) => {
    try {
      // Mark as delivered first (atomic claim)
      const result = await notificationAPI.markDelivered(notif.id);
      if (!result.claimed) return; // Another tab already claimed it
    } catch (err) {
      return; // Failed to claim
    }

    // Browser push
    if (config?.push_enabled && permissionState === 'granted') {
      try {
        const contactName = notif.first_name
          ? `${notif.first_name} ${notif.last_name || ''}`.trim()
          : notif.nombre || notif.contacto_phone;

        new Notification(
          notif.notification_type === 'pre_15min' ? 'Seguimiento en 10 min' : 'Seguimiento AHORA',
          {
            body: contactName + (notif.hint ? ` - ${notif.hint}` : ''),
            icon: '/favicon.ico',
            tag: `notif-${notif.id}`,
            requireInteraction: true
          }
        );
      } catch (err) {
        console.warn('Browser notification failed:', err);
      }
    }

    // Sound
    if (config?.sound_enabled) {
      playNotificationSound();
    }

    // Toast: add to state (NotificationContainer renders these)
    if (config?.toast_enabled) {
      setNotifications(prev => {
        const exists = prev.some(n => n.id === notif.id);
        if (exists) return prev;
        return [...prev, { ...notif, deliveredAt: Date.now() }];
      });
    }
  }, [config, permissionState]);

  // Stable ref for deliverNotification (avoids restarting interval on config change)
  const deliverRef = useRef(deliverNotification);
  useEffect(() => { deliverRef.current = deliverNotification; }, [deliverNotification]);

  // Poll for pending notifications every 5s (stable interval, never restarts)
  useEffect(() => {
    const poll = async () => {
      try {
        const pending = await notificationAPI.getPending();
        for (const notif of pending) {
          await deliverRef.current(notif);
        }
      } catch (err) {
        // Silently ignore polling errors
      }
    };

    poll(); // Initial check
    pollRef.current = setInterval(poll, 5000);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Actions
  const completeNotification = useCallback(async (notifId) => {
    try {
      await notificationAPI.performAction(notifId, 'complete');
      setNotifications(prev => prev.filter(n => n.id !== notifId));
    } catch (err) {
      console.error('Complete notification failed:', err);
    }
  }, []);

  const snoozeNotification = useCallback(async (notifId, minutes = 15) => {
    try {
      await notificationAPI.performAction(notifId, 'snooze', minutes);
      setNotifications(prev => prev.filter(n => n.id !== notifId));
    } catch (err) {
      console.error('Snooze notification failed:', err);
    }
  }, []);

  const dismissNotification = useCallback(async (notifId) => {
    setNotifications(prev => prev.filter(n => n.id !== notifId));
    // Also mark as completed in DB to prevent it from coming back
    try {
      await notificationAPI.performAction(notifId, 'complete');
    } catch (_) {} // Best effort - UI already removed it
  }, []);

  // Acknowledge: descarte SOLO local (sin escribir BD). Para el aviso pre_15min:
  // "Entendido"/"Ir al contacto" NO debe completar ni posponer el seguimiento —
  // la alerta del momento exacto debe saltar igual a su hora. La notif ya fue
  // markDelivered al mostrarse, así que no reaparece en el polling (getPending
  // sólo trae status='pending').
  const acknowledgeNotification = useCallback((notifId) => {
    setNotifications(prev => prev.filter(n => n.id !== notifId));
  }, []);

  // No auto-dismiss: notifications persist until user acts (complete/snooze/dismiss)

  // Config update
  const updateConfig = useCallback(async (newConfig) => {
    try {
      const updated = await notificationAPI.updateConfig(newConfig);
      setConfig(updated);
      return updated;
    } catch (err) {
      console.error('Update config failed:', err);
      throw err;
    }
  }, []);

  return {
    notifications,
    config,
    permissionState,
    completeNotification,
    snoozeNotification,
    dismissNotification,
    acknowledgeNotification,
    updateConfig
  };
}
